import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { FC } from 'react';
import { NavLink } from 'react-router-dom';
import { inFlightRunStatusText, useRunEmailExecBrief } from '../../../../../../hooks/useRunEmailExecBrief';
import { useUserSession } from '../../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../../models/Logging';
import { useEmailExecBriefsQuery } from '../../../../../../queries/EmailExecAssistant/useEmailExecBriefsQuery';
import {
	selectBoundEmailExecTemplate,
	useEmailExecAssistantTemplatesQuery,
} from '../../../../../../queries/EmailExecAssistant/useEmailExecTemplates';
import { useLatestEmailExecDigestQuery } from '../../../../../../queries/EmailExecAssistant/useLatestEmailExecDigestQuery';
import { Button } from '../../../../../components-v2/Button';
import { ROUTES } from '../../../../../constants/routes';
import { runListDateParts } from '../../../../../pages/EmailExecBriefs/helpers';
import { getScheduleTimezone } from '../../../../settings/PersonalSettings/EmailExecAssistantSettings/scheduleUtils';
import { MailSparkleIcon } from '../../../../svgs/icons/MailSparkleIcon';
import { AgentPanelFooter, AgentPanelItem, AgentPanelLink, AgentPanelMessage } from '../../AgentPanel';
import { agentTileState } from '../../AgentTile';
import { styleSheet } from '../../styles';
import { briefExcerpt, briefReviewCount, briefReviewItems, isRecentBrief } from './helpers';

const TELEMETRY_CATEGORY = 'DashboardEmailExecRow';
const PANEL_ITEM_LIMIT = 3;
const KIND_DOTS = {
	action: styleSheet.actionDot,
	awaiting: styleSheet.awaitingDot,
	draft: styleSheet.draftDot,
	risk: styleSheet.riskDot,
};

export const useEmailExecAgent = () => {
	const userSession = useUserSession();
	const available = !!userSession.account?.features?.emailExec?.available;
	const templatesQuery = useEmailExecAssistantTemplatesQuery({
		enabled: available,
		meta: { errorLogging: { category: TELEMETRY_CATEGORY, action: 'DashboardEmailExecRowLoadTemplates-Error' } },
	});
	const template = selectBoundEmailExecTemplate(templatesQuery.data ?? []);
	const digestQuery = useLatestEmailExecDigestQuery({
		enabled: !!template,
		meta: { errorLogging: { category: TELEMETRY_CATEGORY, action: 'LoadLatestDigest-Error' } },
	});
	const briefsQuery = useEmailExecBriefsQuery({ enabled: !!template });
	const { runBrief, isRunning, inFlightRun } = useRunEmailExecBrief({ enabled: !!template, source: 'dashboardCard' });

	if (!available || templatesQuery.isError || (!templatesQuery.isPending && !template)) {
		return null;
	}

	const agent = {
		key: 'emailExec',
		name: 'Email Exec',
		icon: <MailSparkleIcon />,
		iconStyles: styleSheet.emailExecIcon,
	};
	if (!template || digestQuery.isPending) {
		return { ...agent, tile: null, panel: null };
	}
	if (digestQuery.isError) {
		return {
			...agent,
			tile: agentTileState('Couldn’t load', { isError: true }),
			panel: (
				<AgentPanelMessage
					action={
						<Button size='sm' variant='secondary' onClick={() => digestQuery.refetch()}>
							Try again
						</Button>
					}
				>
					We couldn’t load your latest brief.
				</AgentPanelMessage>
			),
		};
	}

	const runNote = isRunning && inFlightRun ? inFlightRunStatusText(inFlightRun) : null;
	const latestRunFailed = briefsQuery.data?.[0]?.status === 'Failed';
	const runNow = () => runBrief(template.id);
	const digest = digestQuery.data;
	if (!digest) {
		return {
			...agent,
			tile: agentTileState(isRunning ? 'Running your first brief…' : 'Run your first brief'),
			panel: (
				<EmailExecFirstRunPanel
					isRunning={isRunning}
					latestRunFailed={latestRunFailed}
					note={runNote}
					onRunNow={runNow}
				/>
			),
		};
	}

	const reviewCount = briefReviewCount(digest);
	const timeZone = getScheduleTimezone(userSession.user);
	let tile =
		reviewCount > 0 ? agentTileState('to review', { count: reviewCount }) : agentTileState('Nothing to review');
	if (digest.runDate && !isRecentBrief(digest.runDate, timeZone)) {
		tile = agentTileState(`Last brief ${runListDateParts(digest.runDate, timeZone).primary}`);
	}
	return {
		...agent,
		tile,
		panel: (
			<EmailExecLivePanel
				digest={digest}
				isRunning={isRunning}
				latestRunFailed={latestRunFailed}
				note={runNote}
				onRunAgain={runNow}
			/>
		),
	};
};

const EmailExecFailedRunNote: FC = () => {
	const { logEvent } = useEventLogging(TELEMETRY_CATEGORY);
	return (
		<p className={css(styleSheet.panelNote)}>
			<NavLink
				className={css(styleSheet.failureNote)}
				to={ROUTES.emailExec.briefs}
				onClick={() => logEvent('ViewHistoryClicked', { from: 'failureNote' })}
			>
				Your latest run didn’t finish — view history
			</NavLink>
		</p>
	);
};

const EmailExecFirstRunPanel: FC<{
	isRunning: boolean;
	latestRunFailed: boolean;
	note: string | null;
	onRunNow: () => void;
}> = ({ isRunning, latestRunFailed, note, onRunNow }) => {
	const { logEvent } = useEventLogging(TELEMETRY_CATEGORY);
	return (
		<>
			{latestRunFailed && !note ? <EmailExecFailedRunNote /> : null}
			<AgentPanelMessage
				action={
					<Button size='sm' onClick={onRunNow} isLoading={isRunning} disabled={isRunning}>
						Run now
					</Button>
				}
			>
				{note ?? 'You’re all set up — run your first brief.'}
			</AgentPanelMessage>
			<AgentPanelFooter>
				<AgentPanelLink to={ROUTES.emailExec.settings} onClick={() => logEvent('ScheduleLinkClicked')}>
					Set a schedule
				</AgentPanelLink>
			</AgentPanelFooter>
		</>
	);
};

const EmailExecLivePanel: FC<{
	digest: Api.IEmailExecDigest;
	isRunning: boolean;
	latestRunFailed: boolean;
	note: string | null;
	onRunAgain: () => void;
}> = ({ digest, isRunning, latestRunFailed, note, onRunAgain }) => {
	const userSession = useUserSession();
	const { logEvent } = useEventLogging(TELEMETRY_CATEGORY);
	const timeZone = getScheduleTimezone(userSession.user);
	const dateParts = digest.runDate ? runListDateParts(digest.runDate, timeZone) : null;
	const items = briefReviewItems(digest).slice(0, PANEL_ITEM_LIMIT);
	const briefPath = digest.id ? `${ROUTES.emailExec.briefs}/${encodeURIComponent(digest.id)}` : ROUTES.emailExec.briefs;

	return (
		<>
			{dateParts ? (
				<p className={css(styleSheet.panelSource)}>{`Latest brief · ${dateParts.primary} · ${dateParts.time}`}</p>
			) : null}
			{note ? <p className={css(styleSheet.panelNote)}>{note}</p> : null}
			{latestRunFailed && !note ? <EmailExecFailedRunNote /> : null}
			{items.length > 0 ? (
				items.map((item, index) => (
					<AgentPanelItem
						key={`${item.label}-${item.name}-${index}`}
						lead={
							<span className={css(styleSheet.statusLead)}>
								<span className={css(styleSheet.kindDot, KIND_DOTS[item.kind])} />
								<span className={css(styleSheet.statusLabel)}>{item.label}</span>
							</span>
						}
						title={item.name}
						meta={item.meta}
						detail={item.detail}
					/>
				))
			) : (
				<p className={css(styleSheet.panelExcerpt)}>
					{(digest.briefMarkdown && briefExcerpt(digest.briefMarkdown)) || 'Nothing in your latest brief needs you.'}
				</p>
			)}
			<AgentPanelFooter>
				<AgentPanelLink to={briefPath} onClick={() => logEvent('ViewBriefingClicked')}>
					View brief
				</AgentPanelLink>
				<Button variant='link' size='sm' styles={[styleSheet.footerLink]} isLoading={isRunning} onClick={onRunAgain}>
					Run it again
				</Button>
			</AgentPanelFooter>
		</>
	);
};
